// File: /var/www/app2/frontend/src/components/MediaLibrary.tsx

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { User } from "../App";

interface MediaItem {
  reportId: number;
  year: number;
  month: number;
  missionaryId: number;
  missionaryName: string;
  country: string;
  filename: string;
  url: string;
  description: string;
  originalName: string;
  mimetype: string;
  size: number;
}

interface MediaResponse {
  items: MediaItem[];
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Props {
  user: User;
}

const MONTHS = [
  "", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MediaLibrary: React.FC<Props> = ({ user }) => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [lightbox, setLightbox] = useState<MediaItem | null>(null);
  const [error, setError] = useState("");

  const fetchMedia = useCallback(async (p: number, q: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get<MediaResponse>("/api/media", {
        params: { page: p, limit: 24, search: q },
      });
      setItems(res.data.items || []);
      setPage(res.data.page ?? 1);
      setPages(res.data.pages ?? 1);
      setTotal(res.data.total ?? 0);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError("Select a country above to browse media.");
      } else {
        setError("Could not load media. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia(1, search);
  }, [search, fetchMedia]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handlePage = (p: number) => {
    fetchMedia(p, search);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!lightbox) return;
    const idx = items.indexOf(lightbox);
    if (e.key === "ArrowRight" && idx < items.length - 1) setLightbox(items[idx + 1]);
    if (e.key === "ArrowLeft" && idx > 0) setLightbox(items[idx - 1]);
    if (e.key === "Escape") setLightbox(null);
  };

  return (
    <div className="container" onKeyDown={handleKeyDown} tabIndex={0}>
      <div style={{ padding: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h2 style={{ margin: 0 }}>Media Library</h2>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: "14px" }}>
              {total.toLocaleString()} photos across all missionary reports
            </p>
          </div>
          <form onSubmit={handleSearch} style={{ display: "flex", gap: "8px" }}>
            <input
              type="text"
              placeholder="Search by missionary or description…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                width: "280px",
              }}
            />
            <button type="submit" className="btn btn-primary" style={{ padding: "8px 16px" }}>
              Search
            </button>
            {search && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: "8px 12px" }}
                onClick={() => { setSearch(""); setSearchInput(""); }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {error && (
          <div style={{ padding: "12px", background: "#fee2e2", color: "#dc2626", borderRadius: "6px", marginBottom: "16px" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#666" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#666" }}>
            {search ? `No photos found matching "${search}"` : "No photos found."}
          </div>
        ) : (
          <>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "12px",
              marginBottom: "24px",
            }}>
              {items.map((item, idx) => (
                <div
                  key={`${item.reportId}-${item.filename}`}
                  onClick={() => setLightbox(item)}
                  style={{
                    cursor: "pointer",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    transition: "transform 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = "scale(1.02)";
                    (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = "scale(1)";
                    (e.currentTarget as HTMLElement).style.boxShadow = "none";
                  }}
                >
                  <div style={{ position: "relative", paddingBottom: "75%", background: "#e5e7eb" }}>
                    <img
                      src={item.url}
                      alt={item.description || item.filename}
                      loading="lazy"
                      style={{
                        position: "absolute",
                        top: 0, left: 0,
                        width: "100%", height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                  <div style={{ padding: "8px 10px" }}>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.missionaryName}
                    </div>
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>
                      {MONTHS[item.month]} {item.year}
                      {item.country && ` · ${item.country}`}
                    </div>
                    {item.description && (
                      <div style={{ fontSize: "11px", color: "#5a6472", marginTop: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: "8px", alignItems: "center" }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePage(page - 1)}
                  disabled={page <= 1}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: "14px", color: "#666" }}>
                  Page {page} of {pages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePage(page + 1)}
                  disabled={page >= pages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "12px",
              overflow: "hidden",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <img
              src={lightbox.url}
              alt={lightbox.description || lightbox.filename}
              style={{ maxWidth: "80vw", maxHeight: "70vh", objectFit: "contain" }}
            />
            <div style={{ padding: "16px 20px", borderTop: "1px solid #e5e7eb" }}>
              <div style={{ fontWeight: 600, fontSize: "15px" }}>{lightbox.missionaryName}</div>
              <div style={{ color: "#666", fontSize: "13px", marginTop: "4px" }}>
                {MONTHS[lightbox.month]} {lightbox.year}
                {lightbox.country && ` · ${lightbox.country}`}
                {" · "}
                <a href={`/reports/${lightbox.reportId}`} style={{ color: "#6366f1" }}>
                  View Report
                </a>
              </div>
              {lightbox.description && (
                <div style={{ marginTop: "6px", fontSize: "14px", color: "#374151" }}>
                  {lightbox.description}
                </div>
              )}
              <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
                <a
                  href={lightbox.url}
                  download={lightbox.originalName || lightbox.filename}
                  className="btn btn-primary"
                  style={{ fontSize: "13px", padding: "6px 14px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Download
                </a>
                <button
                  className="btn btn-secondary"
                  style={{ fontSize: "13px", padding: "6px 14px" }}
                  onClick={() => setLightbox(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
